// Shared types for the OneCenter Large Dataset Download feature (OE-6545).
// These mirror the cross-agent contract at
// work/projects/onecenter-download/contract.md § Shared Types — keep them in
// sync; the backend (.NET) owns the shapes, the frontend mirrors them.

/** Lifecycle of a download task (stored server-side as NVARCHAR(20)). */
export type TaskStatus =
  | 'Queued'
  | 'Running'
  | 'Completed'
  | 'Failed'
  | 'Cancelled';

/** Statuses past which a task will never change again — polling can stop. */
export const TERMINAL_STATUSES: ReadonlyArray<TaskStatus> = [
  'Completed',
  'Failed',
  'Cancelled',
];

export const isTerminalStatus = (status: string): boolean =>
  TERMINAL_STATUSES.includes(status as TaskStatus);

/** One row in the user's download history table (GET /tasks). */
export type DownloadTask = {
  taskId: string; // GUID
  status: TaskStatus;
  createdAt: string; // ISO-8601 UTC
  startedAt: string | null;
  completedAt: string | null;
  rowCount: number | null;
  fileName: string | null;
  errorMessage: string | null;
};

/**
 * Request body shared by POST /validate and POST /tasks. The lookup filters are
 * multi-select (`string[]`, IN-list matched). `clientKeys` optionally narrows to
 * a SUBSET of the caller's authorized clients — the server validates it's ⊆ the
 * caller's OOClientKey claims (out-of-scope → 400) and falls back to all
 * authorized clients when omitted/empty. (The API also still accepts a scalar
 * string for the lookup fields via a server-side converter, but we always send
 * arrays now.)
 */
export type DownloadRequest = {
  startDate: string; // YYYY-MM-DD, required
  endDate: string; // YYYY-MM-DD, required
  clientKeys?: string[] | null;
  chargeId?: string | null;
  chargeCode?: string[] | null;
  modifiers?: string[] | null;
  voidStatus?: string | null;
  providerType?: string[] | null;
  location?: string[] | null;
  pmSystem?: string[] | null;
  patient?: string | null;
  company?: string[] | null;
  payerType?: string[] | null;
  filters?: Record<string, unknown>;
};

/** Response from POST /validate — the microservice's pre-query estimate. */
export type ValidateResult = {
  valid: boolean;
  count: number;
  message: string;
};

/** Response from POST /tasks. */
export type DownloadTaskCreated = {
  taskId: string;
  status: TaskStatus;
  createdAt: string;
};

/** Response from GET /tasks/{id}/downloadUrl — short-lived SAS URL. */
export type DownloadUrl = {
  url: string;
  expiresAt: string;
};

// --- Filter options (GET /filters) ---------------------------------------

export type NameOption = { name: string };
export type CodeNameOption = { code: string; name: string };
export type CodeDescriptionOption = { code: string; description: string | null };
/** The caller's authorized clients (claims-derived); `key` is the OOClientKey. */
export type ClientOption = { key: string; name: string };

/**
 * Lookup options for the filter dropdowns, scoped server-side to the caller's
 * authorized OOClientKeys. `clients` is the caller's own authorized client set
 * (for the Client filter). The other dimensions' values are sent back verbatim
 * on /validate and /tasks (`name`, or `code` for pmSystem / ooChargeCode).
 */
export type FilterOptions = {
  clients: ClientOption[];
  providerTypes: NameOption[];
  locations: NameOption[];
  pmSystems: CodeNameOption[];
  payerTypes: NameOption[];
  companies: NameOption[];
  ooChargeCodes: CodeDescriptionOption[];
};

// --- Wire-shape mappers ---------------------------------------------------
//
// The task endpoints (GET/POST /tasks, /downloadUrl) currently serialize
// PascalCase — their DTOs have no [JsonProperty] attributes and the API
// registers Newtonsoft without a camelCase resolver — while /filters and
// /validate are camelCase. See the backend→frontend handoff (2026-06-18) in
// work/projects/onecenter-download/handoffs.md. These readers tolerate BOTH
// casings so the frontend keeps working once OE-6541's DTOs are aligned.

const pick = <T>(raw: any, camel: string, pascal: string): T =>
  raw?.[camel] ?? raw?.[pascal];

export const toDownloadTask = (raw: any): DownloadTask => ({
  taskId: pick(raw, 'taskId', 'TaskId'),
  status: pick<TaskStatus>(raw, 'status', 'Status'),
  createdAt: pick(raw, 'createdAt', 'CreatedAt'),
  startedAt: pick(raw, 'startedAt', 'StartedAt') ?? null,
  completedAt: pick(raw, 'completedAt', 'CompletedAt') ?? null,
  rowCount: pick(raw, 'rowCount', 'RowCount') ?? null,
  fileName: pick(raw, 'fileName', 'FileName') ?? null,
  errorMessage: pick(raw, 'errorMessage', 'ErrorMessage') ?? null,
});

/** Accepts either a bare array or an `{ items: [...] }` envelope. */
export const toDownloadTaskList = (raw: any): DownloadTask[] => {
  const items = Array.isArray(raw) ? raw : raw?.items ?? raw?.Items ?? [];
  return items.map(toDownloadTask);
};

export const toDownloadTaskCreated = (raw: any): DownloadTaskCreated => ({
  taskId: pick(raw, 'taskId', 'TaskId'),
  status: pick<TaskStatus>(raw, 'status', 'Status'),
  createdAt: pick(raw, 'createdAt', 'CreatedAt'),
});

export const toDownloadUrl = (raw: any): DownloadUrl => ({
  url: pick(raw, 'url', 'Url'),
  expiresAt: pick(raw, 'expiresAt', 'ExpiresAt'),
});
